import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Gift, ArrowLeft, Copy, Share2, MessageSquare, CheckCircle2, Clock, Ban } from "lucide-react";
import useLoyaltyRules from "@/hooks/useLoyaltyRules";

const STATUS_META = {
  pending: { icon: Clock, cls: "bg-amber-50 text-amber-700 border-amber-200", label: "Pending — friend hasn't paid yet" },
  rewarded: { icon: CheckCircle2, cls: "bg-emerald-50 text-emerald-700 border-emerald-200", label: "Rewarded — credit on your account" },
  capped: { icon: Ban, cls: "bg-slate-50 text-slate-600 border-slate-200", label: "Capped — referral cap reached" },
};

export default function ReferralsPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const rules = useLoyaltyRules();
  const bonusPts = Number(rules.companion_install_bonus_points || 0);

  useEffect(() => {
    API.get("/referrals/mine")
      .then(({ data }) => setData(data))
      .catch(() => toast.error("Couldn't load your referrals"))
      .finally(() => setLoading(false));
  }, []);

  const shareUrl = data ? `${window.location.origin}/register?ref=${data.referral_code}` : "";
  const bonusLine = bonusPts > 0
    ? ` I'll also earn ${bonusPts.toLocaleString()} bonus reward points the moment you sign up.`
    : "";
  const message = data
    ? `Join me on GLPFlo for medically-supervised GLP-1 medications. Use my code ${data.referral_code} at signup and we both get $${data.reward_amount} off!${bonusLine} ${shareUrl}`
    : "";

  const copy = (val, label = "Copied") => { navigator.clipboard.writeText(val); toast.success(label); };

  const shareViaSms = () => {
    // OS sms: URI — opens native messaging composer with body pre-filled
    const url = `sms:?body=${encodeURIComponent(message)}`;
    window.location.href = url;
  };

  const shareViaWeb = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: "Join me on GLPFlo", text: message, url: shareUrl });
      } catch (_) { /* user cancelled */ }
    } else {
      copy(message, "Share message copied — paste it anywhere");
    }
  };

  if (loading) {
    return <div className="min-h-screen bg-slate-50 dark:bg-slate-950 p-6 flex items-center justify-center text-slate-400">Loading…</div>;
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950" data-testid="referrals-page">
      <div className="max-w-3xl mx-auto p-6 lg:p-8">
        <button onClick={() => navigate(-1)} className="text-sm text-slate-500 hover:text-slate-900 dark:hover:text-white inline-flex items-center gap-1.5 mb-6" data-testid="referrals-back">
          <ArrowLeft className="w-4 h-4" /> Back to dashboard
        </button>

        {/* Hero */}
        <div className="bg-gradient-to-br from-purple-600 to-pink-600 rounded-3xl p-8 text-white mb-8 relative overflow-hidden">
          <div className="absolute right-0 top-0 w-64 h-64 bg-white/10 rounded-full -mr-32 -mt-32" />
          <div className="absolute right-20 bottom-0 w-40 h-40 bg-white/10 rounded-full -mb-20" />
          <div className="relative">
            <div className="inline-flex items-center gap-2 bg-white/20 rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wider mb-3">
              <Gift className="w-3.5 h-3.5" /> Your referral hub
            </div>
            <h1 className="text-3xl lg:text-4xl font-bold mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Get ${data.reward_amount} for every friend
            </h1>
            <p className="text-white/90 text-sm lg:text-base max-w-lg">
              Share your code. When a friend completes their first paid order, you get <strong>${data.reward_amount} off</strong> applied automatically to your account. Up to <strong>{data.cap}</strong> referrals — that&apos;s <strong>${data.cap * data.reward_amount} in total savings</strong>.
            </p>
            {bonusPts > 0 && (
              <div
                className="mt-4 inline-flex items-start gap-2 rounded-2xl bg-amber-300 text-amber-950 px-4 py-3 shadow-md"
                data-testid="referrals-install-bonus"
              >
                <Gift className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-[10px] uppercase tracking-widest font-bold">Refer + earn (new)</p>
                  <p className="text-sm font-black leading-tight">+{bonusPts.toLocaleString()} bonus points the moment they sign up</p>
                  <p className="text-[11px] mt-0.5 opacity-90">On top of the ${data.reward_amount}/${data.reward_amount} discount — credited immediately, no purchase required.</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-5 text-center" data-testid="referrals-stat-rewarded">
            <p className="text-3xl font-bold text-emerald-600">{data.counts.rewarded}</p>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 uppercase tracking-wider font-bold">Rewarded</p>
          </div>
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-5 text-center" data-testid="referrals-stat-pending">
            <p className="text-3xl font-bold text-amber-600">{data.counts.pending}</p>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 uppercase tracking-wider font-bold">Pending</p>
          </div>
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-5 text-center" data-testid="referrals-stat-remaining">
            <p className="text-3xl font-bold text-purple-600">{data.remaining}</p>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 uppercase tracking-wider font-bold">Slots left</p>
          </div>
        </div>

        {/* Code + share */}
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 mb-8">
          <p className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-2">Your referral code</p>
          <div className="flex items-center gap-3 mb-5 flex-wrap">
            <code className="font-mono font-bold text-3xl text-purple-700 bg-purple-50 dark:bg-purple-900/30 border border-purple-200 dark:border-purple-800 rounded-xl px-4 py-2.5 tracking-widest" data-testid="referrals-code">
              {data.referral_code}
            </code>
            <Button variant="outline" size="sm" onClick={() => copy(data.referral_code, "Code copied")} className="rounded-full" data-testid="referrals-copy-code">
              <Copy className="w-3.5 h-3.5 mr-1" /> Copy code
            </Button>
            <Button variant="outline" size="sm" onClick={() => copy(shareUrl, "Link copied")} className="rounded-full" data-testid="referrals-copy-link">
              <Copy className="w-3.5 h-3.5 mr-1" /> Copy link
            </Button>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <Button onClick={shareViaSms} className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="referrals-share-sms">
              <MessageSquare className="w-4 h-4 mr-2" /> Share via Text
            </Button>
            <Button onClick={shareViaWeb} variant="outline" className="rounded-full border-purple-200 text-purple-700 hover:bg-purple-50" data-testid="referrals-share-other">
              <Share2 className="w-4 h-4 mr-2" /> Share another way
            </Button>
          </div>

          <details className="mt-4">
            <summary className="text-xs text-slate-500 cursor-pointer hover:text-slate-900">Preview message</summary>
            <p className="text-xs text-slate-600 dark:text-slate-400 mt-2 italic bg-slate-50 dark:bg-slate-800/40 rounded-lg p-3 whitespace-pre-wrap">{message}</p>
          </details>
        </div>

        {/* History */}
        <div>
          <h2 className="text-lg font-bold text-slate-900 dark:text-white mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>Referral history</h2>
          {data.referrals.length === 0 ? (
            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-10 text-center" data-testid="referrals-empty">
              <Gift className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-sm text-slate-500">No referrals yet. Share your code to get started!</p>
            </div>
          ) : (
            <ul className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl divide-y divide-slate-100 dark:divide-slate-800">
              {data.referrals.map((r) => {
                const meta = STATUS_META[r.status] || STATUS_META.pending;
                const Icon = meta.icon;
                return (
                  <li key={r.id || r.created_at} className="p-4 flex items-center justify-between gap-4" data-testid={`referrals-row-${r.status}`}>
                    <div>
                      <p className="text-sm font-medium text-slate-900 dark:text-white">{r.referee_name || r.referee_email || "Anonymous referee"}</p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">{r.created_at?.slice(0, 10)}</p>
                    </div>
                    <span className={`inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider rounded-full px-2.5 py-1 border ${meta.cls}`}>
                      <Icon className="w-3 h-3" /> {r.status}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
